import { useEffect, useState } from 'react';
import { Descriptions, Input, InputNumber, Modal } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import MeterDetailsBlade from '../MeterDetailsBlade';
import BladeSection from '../BladeSection';
import styles from '../add-co-party-blade/AddCoPartyBlade.module.css';
import { IPP_VENDOR, UPDATE_IPP_VENDOR } from '@graphql/vendorList.gql';
import { SAVE_COUNTER_PARTY_METER } from '@graphql/counterParty.gql';

interface ViewCoPartyBladeProps {
  open: boolean;
  onClose: () => void;
  coPartyVendorId: number | null;
  onSaved?: () => void;
}

const readOnlyDate = (value?: string | null) =>
  value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : null;

const ViewCoPartyBlade = ({ open, onClose, coPartyVendorId, onSaved }: ViewCoPartyBladeProps) => {
  const [modal, contextHolder] = Modal.useModal();
  const [mounted, setMounted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPercentage, setEditedPercentage] = useState<number | null>(null);
  const [editedPrefix, setEditedPrefix] = useState('');
  const [editedGl, setEditedGl] = useState('');
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const [fetchVendor, { data, loading }] = useLazyQuery(IPP_VENDOR, { fetchPolicy: 'network-only' });
  const [saveCounterPartyMeter] = useMutation(SAVE_COUNTER_PARTY_METER, { errorPolicy: 'all' });
  const [updateIppVendor] = useMutation(UPDATE_IPP_VENDOR, { errorPolicy: 'all' });

  useEffect(() => {
    if (open && coPartyVendorId) {
      setMounted(true);
      setIsEditing(false);
      setInlineError(null);
      fetchVendor({ variables: { id: coPartyVendorId } });
    } else if (!open) {
      const timer = setTimeout(() => setMounted(false), 350);
      return () => clearTimeout(timer);
    }
  }, [open, coPartyVendorId, fetchVendor]);

  if (!mounted || !coPartyVendorId) return null;

  const vendor = data?.ippVendor;
  const isNSPI = vendor?.secondaryVendorType === 'NSPI';

  const handleCancelOrClose = () => {
    if (isEditing) {
      setIsEditing(false);
      setInlineError(null);
    } else {
      onClose();
    }
  };

  const handleFooterAction = async () => {
    if (!isEditing) {
      setInlineError(null);
      setEditedPercentage(vendor?.cibPercentage ?? null);
      setEditedPrefix(vendor?.vendorInvoicePrefix ?? '');
      setEditedGl(vendor?.secondaryIppGlNumber ?? '');
      setIsEditing(true);
      return;
    }

    setInlineError(null);
    if (editedPercentage == null || editedPercentage <= 0 || editedPercentage > 100) {
      setInlineError('Percentage must be between 0 and 100.');
      return;
    }
    if (!editedPrefix.trim()) {
      setInlineError('Vendor Invoice Prefix is required.');
      return;
    }

    const percentageChanged = editedPercentage !== vendor?.cibPercentage;
    const prefixChanged = editedPrefix !== (vendor?.vendorInvoicePrefix ?? '');
    const glChanged = isNSPI && editedGl !== (vendor?.secondaryIppGlNumber ?? '');

    if (!percentageChanged && !prefixChanged && !glChanged) {
      setIsEditing(false);
      return;
    }

    modal.confirm({
      title: 'Confirm these changes',
      content: (
        <>
          {percentageChanged && (
            <div>
              Change Payable to {vendor?.secondaryVendorType} Percentage from{' '}
              <strong>{vendor?.cibPercentage}%</strong> to <strong>{editedPercentage}%</strong>
            </div>
          )}
          {prefixChanged && (
            <div>
              Change Vendor Invoice Prefix from <strong>{vendor?.vendorInvoicePrefix}</strong> to{' '}
              <strong>{editedPrefix}</strong>
            </div>
          )}
          {glChanged && (
            <div>
              Change Co-party GL from <strong>{vendor?.secondaryIppGlNumber || '—'}</strong> to{' '}
              <strong>{editedGl || '—'}</strong>
            </div>
          )}
        </>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      zIndex: 10002,
      onOk: () => doSave({ percentageChanged, prefixChanged, glChanged }),
    });
  };

  const doSave = async ({
    percentageChanged,
    prefixChanged,
    glChanged,
  }: {
    percentageChanged: boolean;
    prefixChanged: boolean;
    glChanged: boolean;
  }) => {
    setSaving(true);
    try {
      if (prefixChanged) {
        const prefixResult = await updateIppVendor({
          variables: {
            id: coPartyVendorId,
            input: { vendorInvoicePrefix: editedPrefix },
          },
        });

        if (prefixResult.errors?.length) {
          setInlineError(prefixResult.errors[0].message);
          return;
        }
      }

      let percentageChangeDeferred = false;
      if (percentageChanged || glChanged) {
        const result = await saveCounterPartyMeter({
          variables: {
            ippId: coPartyVendorId,
            meterNumber: vendor?.sharedMeterNumber,
            percentage: editedPercentage,
            secondaryIppGl: isNSPI && editedGl ? { glNumber: editedGl } : undefined,
          },
        });

        if (result.errors?.length) {
          setInlineError(result.errors[0].message);
          return;
        }

        if (percentageChanged && result.data?.saveCounterPartyMeter?.percentageChangeDeferred) {
          percentageChangeDeferred = true;
        }
      }

      await fetchVendor({ variables: { id: coPartyVendorId } });
      onSaved?.();
      setIsEditing(false);

      if (percentageChangeDeferred) {
        modal.info({
          title: 'Change Scheduled',
          content:
            'These changes will be reflected from the next invoice month, as Prod Stats approval is already completed for the current month.',
          zIndex: 10002,
        });
      }
    } catch (err: unknown) {
      setInlineError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const invoiceDetailFields: Array<[string, string | number]> = vendor
    ? (
        [
          ['Primary Invoice Email', vendor.vendorEmailId],
          ['Additional Invoice Email 1', vendor.vendorEmailId1],
          ['Additional Invoice Email 2', vendor.vendorEmailId2],
          ['Additional Invoice Email 3', vendor.vendorEmailId3],
          ['Purchase Order No', vendor.purchaseOrderNo],
          ['PO Calendar Year', vendor.poCalendarYear],
          ['Commercial Status', vendor.commercialStatus],
          ['Vendor Monthly Due Date', vendor.vendorDueDate],
          ['Date of Status', readOnlyDate(vendor.statusDate)],
          ['Commercial Operation Date', readOnlyDate(vendor.commercialOperationDate)],
        ] as Array<[string, string | number | null | undefined]>
      ).filter((entry): entry is [string, string | number] => entry[1] != null && entry[1] !== '')
    : [];

  return (
    <>
    {contextHolder}
    <MeterDetailsBlade
      open={open}
      title="Co-party Details"
      onClose={handleCancelOrClose}
      monthLabel=""
      handleApply={handleFooterAction}
      applyLabel={isEditing ? 'Save' : 'Edit'}
      saving={saving}
      isDirty
      inlineError={inlineError}
      infoSection={<></>}
    >
      <BladeSection>
        {loading && <div className={styles.plainText}>Loading…</div>}

        {vendor && (
          <>
            <div className={styles.card}>
              <div className={styles.groupLabel}>This is a Co-party vendor</div>
              <div className={styles.plainTextName} style={{ marginBottom: 12 }}>
                {vendor.vendorName}
              </div>
              <div className={styles.readOnlyGroup}>
                <div className={styles.plainText}>Counter Party Type: {vendor.secondaryVendorType}</div>
                <div className={styles.plainText}>
                  Payable to {vendor.secondaryVendorType} Percentage:{' '}
                  {isEditing ? (
                    <InputNumber
                      autoFocus
                      min={0}
                      max={100}
                      step={0.0001}
                      precision={4}
                      value={editedPercentage}
                      onChange={(val) => setEditedPercentage(val)}
                      style={{ width: 140 }}
                    />
                  ) : (
                    `${vendor.cibPercentage}%`
                  )}
                </div>
                {vendor.sharedMeterNumber && (
                  <div className={styles.plainText}>Meter Number: {vendor.sharedMeterNumber}</div>
                )}
                {isNSPI && (
                  <div className={styles.plainText}>
                    Co-party GL:{' '}
                    {isEditing ? (
                      <Input
                        value={editedGl}
                        onChange={(e) => setEditedGl(e.target.value)}
                        placeholder="Enter Co-party GL Number"
                        style={{ width: 200 }}
                      />
                    ) : (
                      vendor.secondaryIppGlNumber || '—'
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.card}>
              <div className={styles.groupLabel}>Vendor Name &amp; Contact Info</div>
              <div className={styles.readOnlyGroup}>
                {vendor.contactName && <div className={styles.plainText}>{vendor.contactName}</div>}
                {vendor.vendorAddress && <div className={styles.plainText}>{vendor.vendorAddress}</div>}
                {vendor.vendorContactNumber && (
                  <div className={styles.plainTextMuted}>{vendor.vendorContactNumber}</div>
                )}
                {vendor.departmentName && <div className={styles.plainTextMuted}>{vendor.departmentName}</div>}
              </div>
            </div>

            <div className={styles.card}>
              <div className={styles.groupLabel}>Invoice &amp; Status Details</div>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Vendor Invoice Prefix">
                  {isEditing ? (
                    <Input
                      value={editedPrefix}
                      onChange={(e) => setEditedPrefix(e.target.value)}
                      placeholder="Enter Prefix (e.g., T1CIB)"
                      maxLength={20}
                      style={{ width: 200 }}
                    />
                  ) : (
                    vendor.vendorInvoicePrefix
                  )}
                </Descriptions.Item>
                {invoiceDetailFields.map(([label, value]) => (
                  <Descriptions.Item key={label} label={label}>
                    {value}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </div>
          </>
        )}
      </BladeSection>
    </MeterDetailsBlade>
    </>
  );
};

export default ViewCoPartyBlade;
